// Canonical shapes for the ballot payload's `election_offices` array. The ballot
// view imports these rather than redeclaring them, mirroring how mobile reads
// them from its shared ballot types.
export interface ElectionOfficeLink {
  url: string;
  label: string;
}

export interface ElectionOffice {
  name: string;
  level: string;
  links: ElectionOfficeLink[];
}

// Only http(s) links are safe to open in a new tab. Shared by the office links
// here and the officeholder URLs in the ballot view, so the rule lives once.
export const isSafeHttpUrl = (url: string): boolean => /^https?:\/\//i.test(url);

// The backend labels office links from a fixed vocabulary (ballot_service's
// `_URL_LABEL_HINTS` / `_authority_links`). Those full labels read as clutter
// inline in a sentence, so each maps to a short i18n key; an unmapped label
// falls back to whatever the API sent.
const SHORT_LABEL_KEYS: Record<string, string> = {
  'Election Office Website': 'website',
  'Check Your Registration Status': 'registrationStatus',
  'Find Your Polling Location': 'pollingLocation',
  'View Sample Ballot': 'sampleBallot',
  'Absentee / Mail-In Voting': 'absentee',
  'Early Voting Information': 'earlyVoting',
};

export type OfficeLink = ElectionOfficeLink & { shortLabelKey?: string };

export interface OfficeLinkGroup {
  name: string;
  level: string;
  links: OfficeLink[];
}

/** Offices that have at least one openable link, with short-label keys attached. */
export const officeLinkGroups = (offices?: ElectionOffice[]): OfficeLinkGroup[] =>
  (offices ?? [])
    .map((office) => ({
      name: office.name,
      level: office.level,
      links: (office.links ?? [])
        .filter((link) => isSafeHttpUrl(link.url))
        .map((link) => ({ ...link, shortLabelKey: SHORT_LABEL_KEYS[link.label] })),
    }))
    .filter((office) => Boolean(office.name) && office.links.length > 0);
